import React, { useEffect, useMemo, useState } from "react";
import { ChartBar } from "@phosphor-icons/react";
import api from "@/lib/api";

const PRESETS = [7, 30, 90];
const FUNNELS = [
  { slug: "", label: "All funnels" },
  { slug: "standard", label: "Standard Form" },
  { slug: "quiz", label: "Quiz-Style Form" },
  { slug: "quiz-v2", label: "Quiz V2 (App-Style)" },
  { slug: "focused", label: "Focused Funnel" },
  { slug: "auto", label: "Car Accident" },
  { slug: "truck", label: "Truck / 18-Wheeler" },
  { slug: "rideshare", label: "Rideshare (Uber / Lyft)" },
  { slug: "motorcycle", label: "Motorcycle" },
  { slug: "injury", label: "Personal Injury (All-in-One)" },
];

function isoDaysAgo(n) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - n);
  return d.toISOString().slice(0, 10);
}

function pct(v) {
  return v == null ? "—" : `${(v * 100).toFixed(1)}%`;
}

function RangePicker({ range, onChange, testIdPrefix }) {
  const btn = "border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white";
  return (
    <div className="flex flex-wrap items-center gap-2">
      {PRESETS.map((n) => (
        <button key={n} type="button" className={btn} data-testid={`${testIdPrefix}-preset-${n}`}
                onClick={() => onChange({ from: isoDaysAgo(n - 1), to: isoDaysAgo(0) })}>
          Last {n} days
        </button>
      ))}
      <input type="date" value={range.from} max={range.to} className="border border-black px-2 py-1 text-xs"
             onChange={(e) => e.target.value && onChange({ ...range, from: e.target.value })}
             data-testid={`${testIdPrefix}-from`} />
      <span className="text-xs">to</span>
      <input type="date" value={range.to} min={range.from} className="border border-black px-2 py-1 text-xs"
             onChange={(e) => e.target.value && onChange({ ...range, to: e.target.value })}
             data-testid={`${testIdPrefix}-to`} />
    </div>
  );
}

const hasActivity = (f) => Boolean(f.visits || f.leads || f.delivered || f.test_leads);

function FunnelCard({ f, testIdPrefix, detailed }) {
  return (
    <div className="border-2 border-black bg-white p-3" data-testid={`${testIdPrefix}-total-${f.funnel}`}>
      <div className="label-mono">{f.title}</div>
      <div className="grid grid-cols-3 gap-2 mt-2">
        <Stat label="Visits" value={f.visits} />
        <Stat label="Leads" value={f.leads} />
        <Stat label="Delivered" value={f.delivered} />
      </div>
      <div className="text-[10px] text-zinc-600 mt-2 font-mono">
        Visit → lead {pct(f.conversion_rate)}
        {detailed && <> · Delivered {pct(f.delivery_rate)}</>}
        {detailed && f.test_leads > 0 && (
          <span className="text-amber-800" data-testid={`${testIdPrefix}-test-${f.funnel}`}>{" · "}{f.test_leads} test</span>
        )}
      </div>
    </div>
  );
}

// Buyer view folds funnels with no activity into one line instead of cards full of zeros.
function FunnelTotals({ funnels, testIdPrefix, buyerView }) {
  const active = buyerView ? funnels.filter(hasActivity) : funnels;
  const idle = buyerView ? funnels.filter((f) => !hasActivity(f)) : [];
  return (
    <>
      {active.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {active.map((f) => <FunnelCard key={f.funnel} f={f} testIdPrefix={testIdPrefix} detailed={buyerView} />)}
        </div>
      )}
      {idle.length > 0 && (
        <div className="border-2 border-dashed border-zinc-400 px-3 py-2 text-[11px] text-zinc-600" data-testid={`${testIdPrefix}-idle`}>
          <b>No visits or leads yet{active.length ? "" : " in these dates"}:</b> {idle.map((f) => f.title).join(", ")}
        </div>
      )}
    </>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="font-chivo font-black text-xl leading-none">{value}</div>
      <div className="text-[9px] uppercase tracking-widest text-zinc-500 mt-1">{label}</div>
    </div>
  );
}

function BuyerCell({ r }) {
  if (!r.buyer_page_id) {
    return <span className="text-zinc-600">No buyer code (neutral visits, unmatched leads)</span>;
  }
  return (
    <div>
      <div className="font-bold">{r.firm_name}</div>
      <div className="text-[10px] font-mono text-zinc-500">
        {r.buyer_code}{r.matched_leads ? ` · ${r.matched_leads} matched from answers` : ""}
      </div>
    </div>
  );
}

function ResultsTable({ rows, testIdPrefix }) {
  const th = "text-left px-3 py-2 text-[10px] uppercase tracking-widest";
  return (
    <div className="overflow-x-auto border-2 border-black bg-white">
      <table className="w-full text-xs min-w-[640px]" data-testid={`${testIdPrefix}-table`}>
        <thead className="bg-zinc-100 border-b-2 border-black">
          <tr>
            <th className={th}>Funnel</th><th className={th}>Buyer</th><th className={th}>Visits</th>
            <th className={th}>Leads</th><th className={th}>Delivered</th>
            <th className={th}>Visit → lead</th><th className={th}>Delivered %</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={`${r.funnel}-${r.buyer_page_id || "none"}`} className="border-b border-zinc-200"
                data-testid={`${testIdPrefix}-row-${r.funnel}-${r.buyer_code || "unmatched"}`}>
              <td className="px-3 py-2 whitespace-nowrap">{r.funnel_title}</td>
              <td className="px-3 py-2"><BuyerCell r={r} /></td>
              <td className="px-3 py-2 font-mono">{r.visits}</td>
              <td className="px-3 py-2 font-mono font-bold">
                {r.leads}
                {r.test_leads > 0 && <span className="ml-1 text-[10px] font-normal text-amber-800" data-testid={`${testIdPrefix}-test-${r.funnel}-${r.buyer_code || "unmatched"}`}>({r.test_leads} test)</span>}
              </td>
              <td className="px-3 py-2 font-mono">{r.delivered}</td>
              <td className="px-3 py-2 font-mono">{pct(r.conversion_rate)}</td>
              <td className="px-3 py-2 font-mono">{pct(r.delivery_rate)}</td>
            </tr>
          ))}
          {!rows.length && (
            <tr><td colSpan={7} className="px-3 py-6 text-center label-mono" data-testid={`${testIdPrefix}-empty`}>
              No visits or leads in this date range.
            </td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function useResults(endpoint, range) {
  const [state, setState] = useState({ data: null, err: "", loading: true });
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const bump = () => setTick((t) => t + 1);
    window.addEventListener("funnel-results:refresh", bump);
    return () => window.removeEventListener("funnel-results:refresh", bump);
  }, []);
  useEffect(() => {
    let alive = true;
    setState((s) => ({ ...s, loading: true, err: "" }));
    api.get(endpoint, { params: { date_from: range.from, date_to: range.to } })
      .then((r) => { if (alive) setState({ data: r.data, err: "", loading: false }); })
      .catch((e) => {
        const detail = e.response?.data?.detail;
        if (alive) setState({ data: null, err: typeof detail === "string" ? detail : "Could not load results", loading: false });
      });
    return () => { alive = false; };
  }, [endpoint, range, tick]);
  return state;
}

export const FunnelResultsPanel = ({ endpoint, testIdPrefix, title, subtitle, showUnmatched = true, buyerView = false }) => {
  const [range, setRange] = useState(() => ({ from: isoDaysAgo(29), to: isoDaysAgo(0) }));
  const [funnel, setFunnel] = useState("");
  const { data, err, loading } = useResults(endpoint, range);
  const rows = useMemo(
    () => (data?.rows || []).filter((r) => (!funnel || r.funnel === funnel) && (showUnmatched || r.buyer_page_id)),
    [data, funnel, showUnmatched],
  );
  const cards = useMemo(
    () => (data?.funnels || []).filter((f) => !buyerView || !funnel || f.funnel === funnel),
    [data, funnel, buyerView],
  );
  return (
    <div className="brut-card mt-8" data-testid={testIdPrefix}>
      <div className="px-4 py-3 border-b border-black flex items-center gap-3 flex-wrap">
        <ChartBar size={16} weight="bold" />
        <div className="font-chivo font-black uppercase tracking-tighter text-sm">{title}</div>
        <div className="label-mono text-zinc-600">{subtitle}</div>
      </div>
      <div className="p-4 flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-3 justify-between">
          <RangePicker range={range} onChange={setRange} testIdPrefix={testIdPrefix} />
          <select value={funnel} onChange={(e) => setFunnel(e.target.value)} data-testid={`${testIdPrefix}-funnel`}
                  className="border-2 border-black px-2 py-1.5 text-xs bg-white">
            {FUNNELS.map((f) => <option key={f.slug} value={f.slug}>{f.label}</option>)}
          </select>
        </div>
        {err && <div className="border-2 border-red-700 bg-red-50 p-3 text-xs" data-testid={`${testIdPrefix}-error`}>{err}</div>}
        {loading && !data && <div className="label-mono" data-testid={`${testIdPrefix}-loading`}>Loading results…</div>}
        {data && <FunnelTotals funnels={cards} testIdPrefix={testIdPrefix} buyerView={buyerView} />}
        {data && !buyerView && <ResultsTable rows={rows} testIdPrefix={testIdPrefix} />}
        <div className="text-[10.5px] text-zinc-600 leading-relaxed" data-testid={`${testIdPrefix}-footnote`}>
          {showUnmatched ? (
            <>
              Visits are counted on links that carry a buyer code (?b=). Leads matched to a buyer from a
              visitor&apos;s answers started on a neutral page, so their visit is counted under &quot;No buyer code&quot;.
              Test and sample-page leads are excluded.
            </>
          ) : (
            <>
              Visits are counted on links that carry your buyer code. Leads matched to you from a visitor&apos;s
              answers count as leads without a visit. Test leads are counted in Leads, marked &quot;test&quot; and never billed.
            </>
          )}
        </div>
      </div>
    </div>
  );
};
